import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LocalizationProvider } from './context/LocalizationContext';
import { ThemeProvider } from './context/ThemeContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { AppToaster } from './components/AppToaster';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Offers } from './pages/Offers';
import { OfferDetails } from './pages/OfferDetails';
import { AccountDetails } from './pages/AccountDetails';
import { Services } from './pages/Services';
import { Feedback } from './pages/Feedback';
import { SuretyEnquiry } from './pages/SuretyEnquiry';
import { Profile } from './pages/Profile';
import { Notifications } from './pages/Notifications';
import { ProtectedRoute } from './components/ProtectedRoute';
import { getDefaultAuthenticatedPath, isMenuEnabled } from './config/navigation';
import { isAuthenticated } from './services/auth';
import { useLocalization } from './context/LocalizationContext';

const upsertMetaTag = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      element.setAttribute(key, value);
    }
  });
  return element;
};

const upsertLinkTag = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      element.setAttribute(key, value);
    }
  });
  return element;
};

const upsertJsonLd = (id, payload) => {
  let element = document.head.querySelector(`#${id}`);
  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.id = id;
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(payload);
  return element;
};

function RuntimeBrandingEffects() {
  const location = useLocation();
  const { language, locale, ogLocale, t, tenantBrand } = useLocalization();

  React.useEffect(() => {
    const isPublicRoute = location.pathname === '/' || location.pathname === '/login';
    const baseTitle = t('common.documentTitle', {}, 'Self Service Portal');
    const description = t('common.metaDescription', {}, 'Secure self service portal');
    const origin = window.location.origin;
    const canonicalHref = isPublicRoute ? `${origin}/login` : `${origin}${location.pathname}`;

    document.title = isPublicRoute ? baseTitle : baseTitle;

    const descriptionEl = document.querySelector('meta[name="description"]');
    if (descriptionEl) {
      descriptionEl.setAttribute('content', description);
    }

    upsertMetaTag('meta[name="robots"]', {
      name: 'robots',
      content: isPublicRoute ? 'index,follow' : 'noindex,nofollow,noarchive',
    });
    upsertMetaTag('meta[property="og:title"]', {
      property: 'og:title',
      content: baseTitle,
    });
    upsertMetaTag('meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    });
    upsertMetaTag('meta[property="og:url"]', {
      property: 'og:url',
      content: canonicalHref,
    });
    upsertMetaTag('meta[property="og:locale"]', {
      property: 'og:locale',
      content: ogLocale,
    });
    upsertMetaTag('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: baseTitle,
    });
    upsertMetaTag('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: description,
    });

    if (isPublicRoute) {
      upsertLinkTag('link[rel="canonical"]', {
        rel: 'canonical',
        href: canonicalHref,
      });

      upsertJsonLd('sspai-website-schema', {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: tenantBrand.fullName,
        url: origin,
        description,
        inLanguage: locale,
        publisher: {
          '@type': 'Organization',
          name: 'YK Innosoft Technologies',
          url: 'https://www.ykinnosoft.com/',
        },
      });
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove();
      document.head.querySelector('#sspai-website-schema')?.remove();
    }
  }, [language, location.pathname, t, tenantBrand.fullName]);

  return null;
}

function App() {
  const defaultAuthenticatedPath = getDefaultAuthenticatedPath();

  return (
    <LocalizationProvider>
      <ThemeProvider>
        <NotificationsProvider>
          <div className="App">
            <BrowserRouter>
              <RuntimeBrandingEffects />
              <Routes>
                <Route path="/login" element={<Login />} />
                {isMenuEnabled('dashboard') && (
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                )}
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <Notifications />
                    </ProtectedRoute>
                  }
                />
                {isMenuEnabled('offers') && (
                  <>
                    <Route
                      path="/offers"
                      element={
                        <ProtectedRoute>
                          <Offers />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/offers/:offerId"
                      element={
                        <ProtectedRoute>
                          <OfferDetails />
                        </ProtectedRoute>
                      }
                    />
                  </>
                )}
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <AccountDetails />
                    </ProtectedRoute>
                  }
                />
                {isMenuEnabled('services') && (
                  <Route
                    path="/services"
                    element={
                      <ProtectedRoute>
                        <Services />
                      </ProtectedRoute>
                    }
                  />
                )}
                {isMenuEnabled('feedback') && (
                  <Route
                    path="/feedback"
                    element={
                      <ProtectedRoute>
                        <Feedback />
                      </ProtectedRoute>
                    }
                  />
                )}
                {isMenuEnabled('surety') && (
                  <Route
                    path="/surety"
                    element={
                      <ProtectedRoute>
                        <SuretyEnquiry />
                      </ProtectedRoute>
                    }
                  />
                )}
                {isMenuEnabled('profile') && (
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                )}
                <Route
                  path="/"
                  element={<Navigate to={isAuthenticated() ? defaultAuthenticatedPath : '/login'} replace />}
                />
                <Route
                  path="*"
                  element={<Navigate to={isAuthenticated() ? defaultAuthenticatedPath : '/login'} replace />}
                />
              </Routes>
              <AppToaster />
            </BrowserRouter>
          </div>
        </NotificationsProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
